<template>
  <div :id="wrapperId" class="editor-page">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />

        <div class="editor-section mb-16">
          <div class="route-container mb-4 section-heading">
            <h1>
              <component
                :is="iconComponent"
                :color="iconColor"
                class="section-icon"
              />
              {{ title }}
            </h1>
          </div>

          <!-- Multiple mode -->
          <div v-if="multiple">
            <div
              v-for="(item, i) in items"
              :key="item.id"
              class="editor-container mb-12"
              :style="{ background: editorBackgroundColor }"
            >
              <div class="text-h6 font-weight-bold">{{ item.title }}</div>

              <GlobalQuillEditor
                :page="`${page}-${i}`"
                :initialData="item"
                :docId="item.id"
                :firestoreCollection="collection"
                :allowed-roles="allowedRoles"
              />
            </div>
          </div>

          <!-- Single mode -->
          <div
            v-else
            class="editor-container"
            :style="{ background: editorBackgroundColor }"
          >
            <!-- Use LanguageAwareEditor for journeyorientation collection -->
            <LanguageAwareEditor
              v-if="collection === 'journeyorientation'"
              :page="page"
              :firestoreCollection="collection"
              :allowed-roles="allowedRoles"
            />
            <!-- Use GlobalQuillEditor for other collections -->
            <GlobalQuillEditor
              v-else
              :page="page"
              :firestoreCollection="collection"
              :allowed-roles="allowedRoles"
            />
          </div>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage.vue'
  import GlobalQuillEditor from '@/components/utilities/GlobalQuillEditor.vue'
  import LanguageAwareEditor from '@/components/utilities/LanguageAwareEditor.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import db from '@/firebase/init'

  export default {
    name: 'EditorPageWrapper',
    components: {
      HowToUseThisPage,
      GlobalQuillEditor,
      LanguageAwareEditor,
    },
    props: {
      wrapperId: String,
      iconComponent: Object,
      title: String,
      page: String,
      collection: String,
      multiple: {
        type: Boolean,
        default: false,
      },
      allowedRoles: {
        type: Array,
        default: () => ['superAdmin'],
      },
    },
    data() {
      return {
        items: [],
      }
    },
    computed: {
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
    },
    async created() {
      if (this.multiple) {
        const snapshot = await db
          .collection(this.collection)
          .orderBy('order')
          .get({ source: 'server' }) // 🔥 forces a fresh server fetch

        this.items = snapshot.docs.map((doc) => {
          const data = doc.data()
          // console.log('✅ Raw Firestore doc:', doc.id, data)
          return { ...data, id: doc.id }
        })

        // Ensure translate flags exist so the CF runs & migrates `message` -> `variants.default.en`
        // (safe to run repeatedly; it's a merge)
        const batch = db.batch()
        snapshot.docs.forEach((doc) => {
          const data = doc.data() || {}
          const needsAuto = data.autoTranslate !== true
          const needsSrc = !data.sourceLocale
          if (needsAuto || needsSrc) {
            batch.set(
              doc.ref,
              {
                autoTranslate: true,
                sourceLocale: data.sourceLocale || 'en',
              },
              { merge: true }
            )
          }
        })
        await batch.commit()
      } else {
        // For single mode, only set autoTranslate flag for collections that use it
        // Skip journeyorientation collection as it now uses language-aware editing
        if (this.collection !== 'journeyorientation') {
          const snapshot = await db
            .collection(this.collection)
            .get({ source: 'server' })

          const batch = db.batch()
          snapshot.docs.forEach((doc) => {
            const data = doc.data() || {}
            const needsAuto = data.autoTranslate !== true
            const needsSrc = !data.sourceLocale
            if (needsAuto || needsSrc) {
              batch.set(
                doc.ref,
                {
                  autoTranslate: true,
                  sourceLocale: data.sourceLocale || 'en',
                },
                { merge: true }
              )
            }
          })
          if (snapshot.docs.length > 0) {
            await batch.commit()
          }
        }
      }
    },
  }
</script>

<style scoped>
  .editor-page :deep(.v-container) {
    width: 95%;
    max-width: 1440px;
    padding: 0;
    padding-bottom: 50%;
  }
  .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .section-icon {
    margin-right: 10px;
  }
</style>
